import React, { useState, useEffect, useContext, ChangeEvent } from 'react';
import {
  RouteComponentProps,
  StaticContext,
  withRouter,
  useParams,
} from 'react-router';
import { ModifiersUtils } from 'react-day-picker';
import SubmitButton from '../components/SubmitButton';
import Title from '../components/Title';
import AddressLookup, {
  Address,
  testPostCode,
  validateAddress,
} from '../components/AddressLookup';
import 'react-day-picker/lib/style.css';
import {
  BoxData,
  Product,
  Dimensions,
  BookingParams,
} from '../models/APIModels';
import Prices from '../components/Prices';
import NavigationStatus from '../components/NavigationStatus';
import useGtmEventTracking from '../components/useGtmEventTracking';
import DayPickerInputValue from '../components/edit/DayPickerInputValue';
import { useDateDisabler } from '../utilities/DateDisabler';
import { displayLoginName } from '../components/user/DisplayLoginName';
import UserContext from '../contexts/UserContext';
import { isOutOfArea, isDifferentArea } from '../utilities/Postcode';
import { toMoney } from '../utilities/MoneyConversion';
import {
  COLLECTION_FEE,
  COLLECTION_SURCHARGE,
  REDELIVER_FEE,
  REDELIVER_SURCHARGE,
} from '../constants';
import { CalendarIcon } from '@heroicons/react/24/solid';

interface CollectionDeliveryProps
  extends RouteComponentProps<undefined, StaticContext, CollectionState> {}

interface CollectionState {
  boxes: BoxData[];
  collectionAddress?: Address;
  collectionDate?: Date;
  deliveryAddress?: Address;
  deliveryDate?: Date;
  storageRequired: boolean;
  suitcases?: Dimensions[];
  suitcaseData?: Product;
  dontKnowAddress?: boolean;
  dontKnowDate?: boolean;
}

interface CollapsibleSectionProps {
  title: string;
  isCollapsed: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  className?: string;
  collapsedNote?: string;
}

function CollapsibleSection({
  title,
  isCollapsed,
  onToggle,
  children,
  className = '',
  collapsedNote,
}: CollapsibleSectionProps) {
  return (
    <div
      className={`collapsible-section ${
        isCollapsed ? 'collapsed' : ''
      } ${className}`}
    >
      <div className="collapsible-header" onClick={onToggle}>
        <Title>{title}</Title>
        <button type="button" className="collapse-toggle">
          <i className={`fas fa-chevron-${isCollapsed ? 'down' : 'up'}`} />
        </button>
      </div>
      {isCollapsed && collapsedNote && <p className="grey">{collapsedNote}</p>}
      <div
        className={`collapsible-content ${
          isCollapsed ? 'collapsed' : 'expanded'
        }`}
      >
        {children}
      </div>
    </div>
  );
}

function validateCollection(
  collectionAddress: Address,
  collectionDate: Date | undefined,
  disabledDays: any[],
) {
  return (
    validateAddress(collectionAddress) &&
    collectionDate &&
    !ModifiersUtils.dayMatchesModifier(collectionDate, disabledDays)
  );
}

function CollectionDeliveryComponent({
  history,
}: CollectionDeliveryProps): JSX.Element {
  const [collectionAddress, setCollectionAddress] = useState<Address>({});
  const [collectionDate, setCollectionDate] = useState<Date>();
  const [deliveryAddress, setDeliveryAddress] = useState<Address>({});
  const [deliveryDate, setDeliveryDate] = useState<Date>();
  const [dontKnowDate, setDontKnowDate] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { route } = useParams<BookingParams>();
  const { boxes, storageRequired, suitcases, suitcaseData } =
    history.location.state || {};

  const [isCollectionCollapsed, setIsCollectionCollapsed] = useState(false);
  const [isReturnCollapsed, setIsReturnCollapsed] = useState(
    storageRequired ? true : false,
  );

  const collectionDisabledDays = useDateDisabler(null, null);
  const deliveryDisabledDays = useDateDisabler(
    deliveryAddress,
    collectionDate ?? null,
  );

  const isCollectionValid = validateCollection(
    collectionAddress,
    collectionDate,
    collectionDisabledDays,
  );

  const isDeliveryValid = storageRequired
    ? !deliveryAddress.postCode ||
      (validateAddress(deliveryAddress) &&
        (dontKnowDate ||
          (deliveryDate &&
            !ModifiersUtils.dayMatchesModifier(
              deliveryDate,
              deliveryDisabledDays,
            ))))
    : validateAddress(deliveryAddress);

  const isValid = isCollectionValid && isDeliveryValid;

  const { user } = useContext(UserContext);

  const gtmEventTracking = useGtmEventTracking();
  useEffect(() => {
    gtmEventTracking({ event: 'booking_step_collection_delivery_address' });
    window.scrollTo(0, 0);
  }, [gtmEventTracking]);

  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    if (name === 'dontKnowDate') {
      setDontKnowDate(checked);

      if (checked) {
        setDeliveryDate(undefined);
      }
    }
  };

  const handleCollectionToggle = () => {
    if (isCollectionCollapsed) {
      setIsCollectionCollapsed(false);
      if (storageRequired) {
        setIsReturnCollapsed(true);
      }
    } else {
      setIsCollectionCollapsed(true);
    }
  };

  const handleReturnToggle = () => {
    if (isReturnCollapsed) {
      setIsReturnCollapsed(false);
      if (storageRequired) {
        setIsCollectionCollapsed(true);
      }
    } else {
      setIsReturnCollapsed(true);
    }
  };

  return (
    <>
      <section className="login-status-bar">
        <div className="status-bar-content">
          <p className="grey">{displayLoginName(user)}</p>
        </div>
      </section>

      <NavigationStatus step={2} />
      <form
        onSubmit={(event) => {
          setIsSubmitting(true);
          event.preventDefault();
          if (isValid) {
            history.push(`/${route}/payment`, {
              boxes,
              collectionAddress,
              collectionDate,
              deliveryAddress: !deliveryAddress.postCode ? {} : deliveryAddress,
              deliveryDate: dontKnowDate ? undefined : deliveryDate,
              dontKnowAddress: !deliveryAddress.postCode,
              dontKnowDate,
              storageRequired,
              suitcases,
              suitcaseData,
            });
          }
        }}
      >
        <CollapsibleSection
          title="Collection Address"
          isCollapsed={isCollectionCollapsed}
          onToggle={handleCollectionToggle}
          className={isCollectionValid ? 'valid' : ''}
        >
          {route !== 'suitcase' && (
            <p className="grey">
              We'll collect your belongings from here <br />
              This is where your packing materials will be delivered 📦
            </p>
          )}
          <div className="all-coll-del">
            <AddressLookup
              address={collectionAddress}
              setAddress={setCollectionAddress}
              onlyUnitedKingdom
              idPrefix="collection"
            >
              {storageRequired &&
                collectionAddress.postCode &&
                isOutOfArea(collectionAddress.postCode) && (
                  <p>There is a surcharge in this area of £5.99</p>
                )}
            </AddressLookup>
            <div className="coll-del-div">
              <div className="title-and-input">
                <div className="title">
                  <section>Collection Date:</section>
                  <CalendarIcon
                    style={{ width: '30px', height: '30px', color: '#604B8D' }}
                  />
                </div>

                <DayPickerInputValue
                  id="collectionDate"
                  handleStateChange={(day) => setCollectionDate(day as any)}
                  disabled={false}
                  disabledDays={collectionDisabledDays}
                />
              </div>
              <div className="description">
                {!suitcases && (
                  <p className="grey">
                    <span>Note:</span> Your empty boxes will be delivered here.
                  </p>
                )}
                <p className="grey">
                  For collection, please make sure your boxes are on the ground
                  floor.
                </p>
              </div>
            </div>
          </div>

          <p>{testPostCode(collectionAddress)}</p>
        </CollapsibleSection>

        <CollapsibleSection
          title={storageRequired ? 'Return Address' : 'Delivery Address'}
          isCollapsed={isReturnCollapsed}
          onToggle={handleReturnToggle}
          className={isDeliveryValid ? 'valid' : ''}
          collapsedNote={
            storageRequired
              ? '(Optional) This is where belongings will be returned, you can tell us this later'
              : undefined
          }
        >
          <p className="grey">
            {storageRequired
              ? '(Optional) This is where belongings will be returned, you can tell us this later'
              : 'This is where your belongings will be delivered to'}
          </p>

          <div className="all-coll-del">
            <AddressLookup
              address={deliveryAddress}
              setAddress={setDeliveryAddress}
              onlyUnitedKingdom
              idPrefix="delivery"
            >
              {storageRequired &&
                deliveryAddress.postCode &&
                collectionAddress.postCode &&
                isDifferentArea(
                  deliveryAddress.postCode,
                  collectionAddress.postCode,
                ) && (
                  <p>
                    There is a surcharge of £3.99 per box to deliver to a
                    different area.
                  </p>
                )}
            </AddressLookup>

            {storageRequired && (
              <div className="coll-del-div">
                <div className="title-and-input">
                  <div className="title">
                    <section>Delivery Date:</section>
                    <CalendarIcon
                      style={{
                        width: '30px',
                        height: '30px',
                        color: '#604B8D',
                      }}
                    />
                  </div>

                  <DayPickerInputValue
                    id="deliveryDate"
                    value={deliveryDate ? deliveryDate.getTime() : undefined}
                    handleStateChange={(day) => {
                      setDeliveryDate(day as any);
                      if (day) {
                        setDontKnowDate(false);
                      }
                    }}
                    disabled={false}
                    disabledDays={deliveryDisabledDays}
                  />
                  <span className="each-tick">
                    <label className="tick-label" htmlFor="dontKnowDate">
                      Don't know date
                    </label>
                    <input
                      name="dontKnowDate"
                      onChange={handleCheckboxChange}
                      type="checkbox"
                      checked={dontKnowDate}
                    />
                  </span>
                </div>
                <div className="description">
                  <p className="grey">
                    <span>Note:</span> You can order your boxes back at any
                    time.
                  </p>
                </div>
              </div>
            )}
          </div>
        </CollapsibleSection>

        <Prices
          boxes={boxes}
          storageRequired={storageRequired}
          suitcases={suitcases}
          suitcaseShippingCost={suitcaseData?.shippingCost}
        />

        <SubmitButton disabled={!isValid} isSubmitting={isSubmitting}>
          Next
          <img alt="" src="/img/right-arrow-circular-button-outline.png" />
        </SubmitButton>

        {storageRequired && (
          <p className="grey">
            Collection of boxes: {toMoney(COLLECTION_FEE)}
            <br />A {toMoney(COLLECTION_SURCHARGE)} surcharge may apply for some
            collection areas.
            <br />
            Redelivery of boxes: {toMoney(REDELIVER_FEE)} per box, to be paid at
            the end of storage period. (1 month minimum storage charge.)
            <br />
            Surcharge of {toMoney(REDELIVER_SURCHARGE)} per box may apply for
            delivering boxes to a city different from the pickup address.
          </p>
        )}
      </form>
    </>
  );
}

export default withRouter(CollectionDeliveryComponent);
